import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useComponentWebSocket } from './GlobalSettingUtils';

export default function ProjectHistoryRetentionSettings() {
  const { t } = useTranslation();
  const [months, setMonths] = useState('3');
  const [savedMonths, setSavedMonths] = useState('3');
  const [loaded, setLoaded] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [status, setStatus] = useState('');
  const dirty = useRef(false);
  const pending = useRef(null);
  const { sendCommand } = useComponentWebSocket({
    configuration_data: ({ data }) => {
      if (!data || typeof data !== 'object') return;
      if (data.status === 'error' || data.error) { setStatus('load_error'); return; }
      setStatus(current => current === 'load_error' ? '' : current);
      setLoaded(true);
      setCanManage(data.canManageDagHistoryRetention === true);
      if (!dirty.current && pending.current === null) {
        const value = String(data.dag_history_retention_months ?? 3);
        setMonths(value);
        setSavedMonths(value);
      }
    },
    update_configuration: ({ data }) => {
      if (pending.current === null) return;
      if (data === 'success') {
        setSavedMonths(pending.current);
        dirty.current = false;
        setStatus('success');
      } else {
        setStatus('error');
      }
      pending.current = null;
    },
  });
  useEffect(() => { sendCommand('configuration_check').catch(() => setStatus('load_error')); }, [sendCommand]);

  const valid = /^\d+$/.test(months) && Number(months) >= 1 && Number(months) <= 120;
  const save = async () => {
    if (!canManage || !valid || pending.current !== null) return;
    pending.current = months;
    setStatus('saving');
    try {
      await sendCommand('update_configuration', { dag_history_retention_months: Number(months) });
    } catch (_) {
      pending.current = null;
      setStatus('error');
    }
  };
  if (loaded && !canManage) return null;
  return <Box sx={{ p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      {t('global_setting.history_retention.title')}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {t('global_setting.history_retention.description')}
    </Typography>
    {status === 'load_error' && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit"
      onClick={() => sendCommand('configuration_check').catch(() => setStatus('load_error'))}>
      {t('dag.history.refresh')}</Button>}>{t('global_setting.history_retention.load_error')}</Alert>}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
      <TextField type="number" label={t('global_setting.history_retention.label')} value={months}
        disabled={!loaded || status === 'saving'} inputProps={{ min: 1, max: 120, step: 1 }}
        error={!valid} helperText={t(`global_setting.history_retention.${valid ? 'default' : 'invalid'}`)}
        onChange={event => { setMonths(event.target.value); dirty.current = true; setStatus(''); }} />
      <Button variant="contained" onClick={save} sx={{ minHeight: 56 }}
        disabled={!loaded || !canManage || !valid || months === savedMonths || status === 'saving'}>
        {t(`global_setting.history_retention.${status === 'saving' ? 'saving' : 'save'}`)}
      </Button>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
      {t('global_setting.history_retention.cleanup')}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {t('global_setting.history_retention.shortening')}
    </Typography>
    {(status === 'success' || status === 'error') && <Alert role="status" severity={status} sx={{ mt: 2 }}>
      {t(`global_setting.history_retention.${status}`, { count: Number(savedMonths) })}
    </Alert>}
  </Box>;
}
